import {
  getBotFormDefaultStopValue,
  getBotFormDefaultTakeProfitValue,
  getDefaultCheckedFields,
  getDefaultStopType,
  getIsReverse,
  getStrategyFromGridsData,
} from "@shared/features/bots/BotForm/common/utils";
import {
  BOT_FORM_KEY_TO_SETTER_NAME,
  channelValue,
  DEFAULT_ADVANCED_SETTINGS_VALUE,
  DEFAULT_GRID_BOT_STRATEGY,
  DEFAULT_OVERRIDE_FIELDS,
  DEFAULT_SIGNALS_BOT_ADVANCED_SETTINGS_VALUE,
  DEFAULT_SIGNALS_BOT_STRATEGY,
  DEFAULT_STOP_LIMIT_PRICE_REDUCTION,
} from "@shared/features/bots/BotForm/common/constants";
import {
  BaseBotFormValues,
  GridType,
  PercentageBaseType,
} from "@shared/features/bots/BotForm/common/types";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType, LongShort, StopType } from "@shared/types";
import { isSymbol } from "@shared/types/utils";
import { useStopType } from "@shared/features/bots/BotForm/common/hooks";
import { cloneDeep } from "lodash";
import { getDefaultGlobalSettings } from "@shared/features/bots/utils";

const useBotFormDefaultValues = () => {
  const {
    botType,
    gridsData,
    setGridsData,
    setTakeProfitStrategy,
    setEntryStrategy,
    longShort,
    addFieldToInitialize,
    formSetters,
    setCheckedFields,
    setOverrideFields,
    isChannelConfiguration,
    account,
    botGeneralData,
    symbolsMetadata,
    isMarginExchange,
  } = useContext(BotFormContext)!;
  const { validStopTypeOptions } = useStopType();
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;

  const resetStrategy = (isEntry: boolean) => {
    let strategy: BaseBotFormValues["entryStrategy"] = undefined;
    if (isSignalsBot) strategy = DEFAULT_SIGNALS_BOT_STRATEGY;
    else if (isGridBot) {
      const updatedGridsData = cloneDeep(gridsData);
      if (updatedGridsData) {
        updatedGridsData.gridType = GridType.ARITHMETIC;
      }
      strategy = getStrategyFromGridsData(
        DEFAULT_GRID_BOT_STRATEGY,
        updatedGridsData,
        getIsReverse(longShort),
        true
      );
      setGridsData(updatedGridsData);
    }
    const setFunction = isEntry ? setEntryStrategy : setTakeProfitStrategy;
    setFunction(strategy);
    addFieldToInitialize?.(isEntry ? "entryStrategy" : "takeProfitStrategy");
  };

  const getBotFormDefaultValue = (
    formKey: keyof BaseBotFormValues
  ): [any, boolean] => {
    let shouldSet = true;
    const isStopType = formKey === "stopType";
    if (formKey === "defaultStop") {
      return [getBotFormDefaultStopValue(botType), true];
    } else if (formKey === "defaultTakeProfit") {
      return [
        getBotFormDefaultTakeProfitValue(botType, isChannelConfiguration),
        true,
      ];
    } else if (isStopType || formKey === "stopLimitPriceReduction") {
      const stopType = getDefaultStopType(
        validStopTypeOptions,
        botGeneralData?.options["Stop Type"]?.[1]
      );
      if (isStopType) return [stopType, true];
      else if (stopType === StopType.LIMIT) {
        return [DEFAULT_STOP_LIMIT_PRICE_REDUCTION.toString(), true];
      }
      return [undefined, true];
    } else if (formKey === "advancedSettings") {
      return [
        isSignalsBot
          ? DEFAULT_SIGNALS_BOT_ADVANCED_SETTINGS_VALUE
          : DEFAULT_ADVANCED_SETTINGS_VALUE,
        true,
      ];
    } else if (
      [
        "entryGracePercent",
        "stopTimeout",
        "entryGraceTargetExchange",
        "takeProfitGraceTargetExchange",
      ].includes(formKey)
    ) {
      return [undefined, true];
    } else if (["entryStrategy", "takeProfitStrategy"].includes(formKey)) {
      const isEntry = formKey === "entryStrategy";
      resetStrategy(isEntry);
      shouldSet = false;
    } else if (formKey === "symbols") {
      if (isSignalsBot && account && !isSymbol(account)) {
        return [
          symbolsMetadata?.getAccountActiveSymbols(account.client_id),
          true,
        ];
      } else {
        shouldSet = false;
      }
    } else if (formKey === "longShort") {
      const longShortOptions = botGeneralData?.options["Long/Short"]?.[0];
      return [
        longShortOptions?.length
          ? [LongShort.LONG, LongShort.SHORT]
          : undefined,
        true,
      ];
    } else if (formKey === "multiSymbolMarginData") {
      return [
        isMarginExchange ? getDefaultGlobalSettings() : channelValue,
        true,
      ];
    } else if (formKey === "amountPercentageBaseType") {
      return [PercentageBaseType.PAIR_AMOUNT, true];
    } else if (formKey === "amountCustomBaseValue") {
      return [undefined, true];
    } else if (formKey === "trailingTakeProfitLastTargetOnly") {
      return [false, true];
    }
    return [isSignalsBot ? channelValue : undefined, shouldSet];
  };

  const resetCheckedFields = () => {
    setCheckedFields(getDefaultCheckedFields(botType, isChannelConfiguration));
  };

  const resetFieldsValues = () => {
    FIELDS_TO_RESET.forEach((fieldName) => {
      const [defaultValue, shouldSet] = getBotFormDefaultValue(fieldName);
      if (shouldSet) {
        const formSetter = formSetters[BOT_FORM_KEY_TO_SETTER_NAME[fieldName]];
        // @ts-ignore
        formSetter?.(defaultValue);
      }
    });
  };

  return () => {
    resetCheckedFields();
    setOverrideFields(DEFAULT_OVERRIDE_FIELDS);
    resetFieldsValues();
  };
};

export default useBotFormDefaultValues;

const FIELDS_TO_RESET: (keyof BaseBotFormValues)[] = [
  "entryStrategy",
  "trailingEntry",
  "entryGracePercent",
  "takeProfitStrategy",
  "trailingTakeProfit",
  "trailingTakeProfitLastTargetOnly",
  "defaultStop",
  "defaultTakeProfit",
  "stopTimeout",
  "stopType",
  "stopLimitPriceReduction",
  "advancedSettings",
  "trailingStopData",
  "cancelOnProfitBeforeEntry",
  "symbols",
  "multiSymbolMarginData",
  "longShort",
  "amountPercentageBaseType",
  "amountCustomBaseValue",
  "entryGraceTargetExchange",
  "takeProfitGraceTargetExchange",
];
